import { useCallback, useContext, useMemo } from 'react'

import { type SanityFrameBuilderSpacerSize } from '@frame-builder/data/sanity/queries/types/spacer'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import {
  FrameBuilderCommittedStateContext,
  FrameBuilderSelectedStateContext,
} from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import { getItemTitle, toRecord } from '@frame-builder/lib/helpers'
import { type ItemPropsWithId } from '@frame-builder/lib/item'
import { getStatePriceWithDiscount } from '@frame-builder/lib/state-price'

import ItemImage from '@frame-builder/components/utility/item-image'
import ItemList from '@frame-builder/components/utility/item-list'
import PriceBadge from '@frame-builder/components/utility/price-badge'
import SpacerSizeDetails from './spacer-size-details'

const SpacerSizeList = () => {
  const { committedState } = useContext(FrameBuilderCommittedStateContext)
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { frameBuilderConfiguration, frameBuilderType } = useContext(
    FrameBuilderSettingsContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { lastValidState } = useContext(FrameBuilderValidationContext)

  const spacerSizes = useMemo(() => {
    return frameBuilderConfiguration.spacerSizes.filter((spacerSize) => {
      return (
        !selectedState.profile ||
        spacerSize.thickness === 0 ||
        spacerSize.mouldingIds?.some(
          (mouldingId) => mouldingId === selectedState.profile?._id,
        )
      )
    })
  }, [frameBuilderConfiguration, selectedState.profile])

  // Calculate frame prices for comparison
  const framePricesWithDiscount = useMemo(() => {
    return toRecord<number>(
      frameBuilderConfiguration.spacerSizes.map((spacerSize) => {
        return {
          [spacerSize._id]: getStatePriceWithDiscount(
            frameBuilderType,
            frameBuilderConfiguration,
            {
              ...committedState,
              spacerSize,
            },
          ),
        }
      }),
    )
  }, [committedState, frameBuilderConfiguration, frameBuilderType])

  const selectSize = useCallback(
    (spacerSize: SanityFrameBuilderSpacerSize) => {
      dispatchSelectedState({
        type: 'spacer',
        size: spacerSize,
      })
    },
    [dispatchSelectedState],
  )
  const handleChange = useCallback(
    (id: string) => {
      const newSpacerSize = frameBuilderConfiguration.spacerSizes.find(
        (spacerSize) => spacerSize._id === id,
      )

      if (newSpacerSize) {
        selectSize(newSpacerSize)
      }
    },
    [frameBuilderConfiguration, selectSize],
  )

  return (
    <ItemList
      defaultValue={selectedState.spacerSize?._id}
      onChange={handleChange}
      items={spacerSizes.map((spacerSize) => {
        const title = getItemTitle(frameBuilderStrings, spacerSize.title ?? '')

        const imageElement = <ItemImage image={spacerSize.icon} alt={title} />
        const subtitleElement = <>{spacerSize.subtitle}</>
        let descriptionElement: JSX.Element | undefined
        const detailsElement = (
          <SpacerSizeDetails spacerSizeId={spacerSize._id} />
        )
        let badgeElement: JSX.Element | undefined

        if (
          selectedState.spacerSize &&
          selectedState.spacerSize._id !== spacerSize._id
        ) {
          badgeElement = (
            <>
              <PriceBadge
                price={
                  framePricesWithDiscount[spacerSize._id] -
                  framePricesWithDiscount[selectedState.spacerSize._id]
                }
                showPlusSign
              />
            </>
          )
        }

        if (spacerSize.thickness > 0) {
          descriptionElement = (
            <figure
              className="w-6 h-6"
              style={{
                backgroundColor:
                  selectedState.spacerColor?.color?.hex ??
                  lastValidState.spacerColor?.color?.hex,
              }}
            />
          )
        }

        const item: ItemPropsWithId = {
          id: spacerSize._id,
          title,
          imageElement,
          subtitleElement,
          descriptionElement,
          detailsElement,
          badgeElement,
          error: lastValidState.spacerSize?._id === spacerSize._id,
        }

        return item
      })}
    />
  )
}

export default SpacerSizeList
